/**
 * Helpers Utility Module
 * Common helper functions used across the application
 */

export const withTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number = 10000,
  errorMessage: string | null = null,
): Promise<T> => {
  const message = errorMessage || `Request timeout after ${timeoutMs}ms`;
  let timeoutId: ReturnType<typeof setTimeout>;

  return new Promise<T>((resolve, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);

    promise.then(
      (value) => {
        clearTimeout(timeoutId);
        resolve(value);
      },
      (error) => {
        clearTimeout(timeoutId);
        reject(error);
      },
    );
  });
};

export const computeEmailsCampaignId = (
  campaignId: string | null | undefined,
): string | null => {
  if (!campaignId) return null;

  const campaignIdStr = String(campaignId).trim();

  // Derive the year from the campaign ID when present.
  const yearMatch = campaignIdStr.match(/_(\d{4})$/i);
  if (!yearMatch) return null;
  const year = yearMatch[1];

  // Handle _Standard_YYYY pattern (case-insensitive)
  if (campaignIdStr.match(/_Standard_\d{4}$/i)) {
    return campaignIdStr.replace(/_Standard_\d{4}$/i, `_Emails_${year}`);
  }

  // For other formats, append _Emails_YYYY
  return campaignIdStr + `_Emails_${year}`;
};

export const escapeSqlValue = (
  value: unknown,
  isArray: boolean = false,
): string => {
  if (value === null || value === undefined) {
    return "NULL";
  }

  if (isArray || Array.isArray(value)) {
    const arrayValues = normalizeSqlArrayValues(value);
    const escapedArray = arrayValues.map((item) => {
      const str = String(item)
        .replace(/\\/g, "\\\\")
        .replace(/"/g, '\\"')
        .replace(/'/g, "''");
      if (/[\s,'"\\{}]/.test(str)) {
        return `"${str}"`;
      }
      return str;
    });
    return `'{${escapedArray.join(",")}}'::text[]`;
  }

  if (typeof value === "object" && !Array.isArray(value)) {
    return `'${JSON.stringify(value).replace(/'/g, "''")}'`;
  }

  return `'${String(value).replace(/'/g, "''")}'`;
};

const normalizeSqlArrayValues = (value: unknown): unknown[] => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value !== "string") {
    return [value];
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return [];
  }

  // Editable array fields can arrive as JSON text or plain comma-separated text.
  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      return [value];
    }
  }

  return trimmed.split(",").map((item) => item.trim());
};

export const escapeSqlLikePattern = (str: string | null | undefined): string => {
  if (!str) return "";
  return String(str).replace(/[%_\\]/g, "\\$&");
};

export const generateCampaignKey = (campaignId: string | null | undefined): string => {
  if (!campaignId) return "";

  return campaignId
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-20\d{2}$/, ""); // Remove any year suffix (2024, 2025, 2026, etc.)
};

/**
 * Returns a friendly display name from a campaign name/title.
 * Strips underscores, "Standard"/"Webinar"/"Emails" labels, and year suffixes.
 * e.g. "TestCo_Standard_2026" → "TestCo", "My Campaign Webinar 2025" → "My Campaign"
 */
export const friendlyCampaignName = (name: string | null | undefined): string => {
  if (!name) return "";
  return name
    .replace(/_/g, " ")
    .replace(/\s*(Standard|Webinar|Emails|SearchOnly|Search Only)\s*\d{0,4}\s*$/i, "")
    .trim();
};

export const debounce = <T extends (...args: Parameters<T>) => void>(
  func: T,
  wait: number = 300,
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout>;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const throttle = <T extends (...args: Parameters<T>) => void>(
  func: T,
  limit: number = 300,
): ((...args: Parameters<T>) => void) => {
  let inThrottle = false;

  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
};

export const deepClone = <T>(obj: T): T => {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime()) as T;
  }

  if (Array.isArray(obj)) {
    return obj.map(deepClone) as T;
  }

  return Object.fromEntries(
    Object.entries(obj as Record<string, unknown>).map(([key, value]) => [key, deepClone(value)]),
  ) as T;
};

export const deepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;

  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") {
    return false;
  }

  if (a instanceof Date || b instanceof Date) {
    return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
  }

  if (Array.isArray(a) !== Array.isArray(b)) return false;

  const keysA = Object.keys(a as Record<string, unknown>);
  const keysB = Object.keys(b as Record<string, unknown>);

  if (keysA.length !== keysB.length) return false;

  return keysA.every((key) =>
    deepEqual(
      (a as Record<string, unknown>)[key],
      (b as Record<string, unknown>)[key],
    ),
  );
};

export const generateId = (prefix: string = ""): string => {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 9);
  return prefix ? `${prefix}-${timestamp}-${randomPart}` : `${timestamp}-${randomPart}`;
};

export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000,
): Promise<T> => {
  let lastError: Error | undefined;

  for (let i = 0; i <= maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      if (i < maxRetries) {
        const delay = baseDelay * Math.pow(2, i);
        await sleep(delay);
      }
    }
  }

  throw lastError;
};

export const groupBy = <T extends Record<string, unknown>>(
  array: T[],
  key: string | ((item: T) => string),
): Record<string, T[]> => {
  if (!Array.isArray(array)) return {};

  return array.reduce<Record<string, T[]>>((groups, item) => {
    const groupKey = typeof key === "function" ? key(item) : String(item[key] ?? "undefined");
    const groupKeyStr = String(groupKey);

    if (!groups[groupKeyStr]) {
      groups[groupKeyStr] = [];
    }

    groups[groupKeyStr].push(item);
    return groups;
  }, {});
};

export const pick = <T extends Record<string, unknown>, K extends string>(
  obj: T | null | undefined,
  keys: K[],
): Partial<Pick<T, K & keyof T>> => {
  if (!obj || typeof obj !== "object") return {};

  return keys.reduce<Record<string, unknown>>((result, key) => {
    if (key in obj) {
      result[key] = obj[key as keyof T];
    }
    return result;
  }, {}) as Partial<Pick<T, K & keyof T>>;
};

export const omit = <T extends Record<string, unknown>, K extends string>(
  obj: T | null | undefined,
  keys: K[],
): Omit<T, K & keyof T> => {
  if (!obj || typeof obj !== "object") return {} as Omit<T, K & keyof T>;

  const keysSet = new Set<string>(keys);

  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => !keysSet.has(key)),
  ) as Omit<T, K & keyof T>;
};

export const isEmpty = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
};

export const safeJsonParse = <T = unknown>(
  jsonString: string | null | undefined,
  defaultValue: T | null = null,
): T | null => {
  if (!jsonString) return defaultValue;

  try {
    return JSON.parse(jsonString) as T;
  } catch {
    return defaultValue;
  }
};

export const deriveCompanyNameFromUrl = (url: string): string | null => {
  const trimmedUrl = url.trim();
  if (!trimmedUrl) return null;

  try {
    const parseableUrl = /^[a-z][a-z\d+\-.]*:\/\//i.test(trimmedUrl)
      ? trimmedUrl
      : `https://${trimmedUrl}`;
    const hostname = new URL(parseableUrl).hostname.replace(/^www\./i, "");
    const [firstPart] = hostname.split(".");
    if (!firstPart) return null;
    return firstPart
      .split(/[-_]/)
      .map(s => s.charAt(0).toUpperCase() + s.slice(1))
      .join(" ");
  } catch {
    return null;
  }
};

export const isValidUrl = (str: string): boolean => {
  try {
    const u = new URL(str);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";

    try {
      document.body.appendChild(textArea);
      textArea.select();
      return document.execCommand("copy");
    } finally {
      document.body.removeChild(textArea);
    }
  } catch (err) {
    console.error("Failed to copy to clipboard:", err);
    return false;
  }
};
